import { useCallback, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { read, utils } from 'xlsx'
import { XMLParser } from 'fast-xml-parser'
import { useAppForm, FormField, FileInput, SelectInput } from '@tandem/ui/form'
import $type from '@tandem/ui/type.css'
import $form from '@tandem/ui/form.css'
import $table from '@tandem/ui/table.css'
import $layout from '@tandem/ui/layout.css'
import $ from './-ImportPage.css'
import { useTRPC } from '@/trpc'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import Spinner from '@tandem/ui/Spinner'
import { head } from 'lodash'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronsLeftRightIcon, ChevronsRightLeftIcon, ExpandIcon, FunnelIcon, FunnelX, FunnelXIcon, OctagonXIcon, XIcon } from 'lucide-react'

type Row = Record<string, unknown>

let IMAGE_RE = /\.(?:jpe?g|png|gif|webp|avif|svg|bmp|tiff?)(?:[?#]|$)/i
let URL_RE = /^https?:\/\/\S+$/i

function CellValue({ value }: { value: string }) {
  if (IMAGE_RE.test(value)) {
    let urls = value.split(/,\s*/).filter(u => IMAGE_RE.test(u))
    if (urls.length > 0) {
      return (
        <span class={$.thumbCell}>
          {urls.map((url, i) => (
            <a
              key={i}
              href={url}
              target='_blank'
              rel='noreferrer noopener'
              onClick={(e) => e.stopPropagation()}
            >
              <img src={url} class={$.thumb} loading='lazy' alt='' />
            </a>
          ))}
        </span>
      )
    }
  }
  let trimmed = value.trim()
  if (URL_RE.test(trimmed)) {
    return (
      <a
        href={trimmed}
        target='_blank'
        rel='noreferrer noopener'
        class={$type.underline}
        onClick={(e) => e.stopPropagation()}
      >
        {value}
      </a>
    )
  }
  return <>{value}</>
}

let ALLOWED_EXTENSIONS = ['.csv', '.xls', '.xlsx', '.xml']

function parseYmlCatalog(text: string): { headers: string[]; rows: Row[] } | null {
  let parser = new XMLParser({
    ignoreAttributes: false,
    isArray: (name) => ['category', 'offer', 'param', 'picture'].includes(name)
  })

  let doc = parser.parse(text)
  if (!doc.yml_catalog) return null

  let shop = doc.yml_catalog.shop
  if (!shop?.categories?.category || !shop?.offers?.offer) return null

  // Build category map
  let catMap = new Map<string, { name: string; parentId: string | null }>()
  for (let cat of shop.categories.category) {
    catMap.set(String(cat['@_id']), {
      name: String(cat['#text'] ?? ''),
      parentId: cat['@_parentId'] != null ? String(cat['@_parentId']) : null
    })
  }

  // Build breadcrumbs
  let breadcrumbs = new Map<string, string>()
  for (let [id] of catMap) {
    let parts: string[] = []
    let current: string | null = id
    let visited = new Set<string>()
    while (current && catMap.has(current) && !visited.has(current)) {
      visited.add(current)
      let cat = catMap.get(current)!
      parts.unshift(cat.name)
      current = cat.parentId
    }
    breadcrumbs.set(id, parts.join(' / '))
  }

  // Flatten offers
  let rows: Row[] = []
  let allKeys = new Set<string>()

  for (let offer of shop.offers.offer) {
    let row: Row = {}

    for (let [key, value] of Object.entries(offer as Record<string, any>)) {
      if (key.startsWith('@_')) {
        row[key.slice(2)] = value
      } else if (key === 'categoryId') {
        row['category'] = breadcrumbs.get(String(value)) ?? String(value)
      } else if (key === 'param') {
        let params = Array.isArray(value) ? value : [value]
        for (let p of params) {
          row[p['@_name'] ?? 'param'] = p['#text'] ?? ''
        }
      } else if (key === 'picture') {
        let pics = (Array.isArray(value) ? value : [value]) as string[]
        row['picture'] = pics.join(', ')
      } else {
        row[key] = value
      }
    }

    for (let k of Object.keys(row)) allKeys.add(k)
    rows.push(row)
  }

  // Known fields first, then rest alphabetically
  let knownOrder = [
    'id',
    'vendor',
    'model',
    'vendorCode',
    'price',
    'currencyId',
    'category',
    'picture',
    'url',
    'description'
  ]
  let headers = [
    ...knownOrder.filter((f) => allKeys.has(f)),
    ...Array.from(allKeys)
      .filter((f) => !knownOrder.includes(f))
      .sort()
  ]

  return { headers, rows }
}

function getExtension(name: string) {
  let i = name.lastIndexOf('.')
  return i >= 0 ? name.slice(i).toLowerCase() : ''
}

export const Route = createFileRoute('/catalog/import/add')({
  component: ImportUploadPage,
})

function ImportUploadPage() {
  let [headers, setHeaders] = useState<string[]>([])
  let [rows, setRows] = useState<Row[]>([])
  let [error, setError] = useState<string | null>(null)
  let [selected, setSelected] = useState<Set<number>>(new Set())

  let trpc = useTRPC()
  let navigate = useNavigate()

  let { data: brands } = useQuery(trpc.brand.list.queryOptions())
  let createImport = useMutation(trpc.import.upload.mutationOptions())

  let form = useAppForm({
    defaultValues: {
      file: null as File | null,
      brand: null as string | null
    },
    canSubmitWhenInvalid: true,
    validators: {
      async onSubmitAsync() {
        if (headers.length === 0 || rows.length === 0) return
        try {
          let brandId = form.getFieldValue('brand')
          let file = form.getFieldValue('file')
          let submitRows = selected.size > 0 ? rows.filter((_, i) => selected.has(i)) : rows
          let { id } = await createImport.mutateAsync({
            header: headers,
            rows: submitRows,
            brand_id: brandId ? Number(brandId) : null,
            original_filename: file?.name ?? null
          })
          navigate({ to: '/catalog/import/$id', params: { id: String(id) } })
        } catch (e) {
          console.error(e)
        }
      }
    }
  })

  async function handleFileChange(file: File | File[] | null) {
    if (Array.isArray(file)) return
    setError(null)
    setHeaders([])
    setRows([])
    setSelected(new Set())

    if (!file) return

    let ext = getExtension(file.name)
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError('Неподдерживаемый формат. Допустимые: .csv, .xls, .xlsx')
      return
    }

    try {
      if (ext.startsWith('.xls')) {
        let buffer = await file.arrayBuffer()
        let workbook = read(buffer, { type: 'array' })
        let sheet = workbook.Sheets[workbook.SheetNames[0]]

        let data: Row[] = utils.sheet_to_json(sheet)

        if (data.length === 0) {
          setError('Файл пуст')
          return
        }

        setHeaders(Object.keys(data[0]).map(String))
        setRows(data)
      } else if (ext === '.xml') {
        let text = await file.text()
        let result = parseYmlCatalog(text)
        if (!result) {
          setError('Неподдерживаемый формат XML. Поддерживается формат Яндекс Маркет.')
          return
        }
        setHeaders(result.headers)
        setRows(result.rows)
      }
    } catch (e) {
      console.error(e)
      setError('Не удалось прочитать файл.')
    }
  }

  let withData = useMemo(() => headers.length > 0, [headers])

  let [filters, setFilters] = useState({})
  let toggleFilter = useCallback((header, val) => {
    setFilters(filters => ({
      ...filters,
      [header]: filters[header] === val ? undefined : val
    }))
  }, [rows])

  let [pinnedOrder, setPinnedOrder] = useState<number[] | null>(null)

  let visibleRows = useMemo(() => {
    let filtered = rows
      .map((row, i) => ({ row, i }))
      .filter(({ row }) => !Object.keys(filters).find(header => row[header] !== filters[header] && filters[header] !== undefined))
    if (pinnedOrder) {
      let orderMap = new Map(pinnedOrder.map((idx, pos) => [idx, pos]))
      filtered.sort((a, b) => (orderMap.get(a.i) ?? a.i) - (orderMap.get(b.i) ?? b.i))
    }
    return filtered
  }, [rows, filters, pinnedOrder])
  
  let scrollerRef = useRef<HTMLDivElement>(null)

  let rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => 33,
    overscan: 20,
  })

  return (
    <div class={$.page}>
      <div class={clsx($type.breadcrumb, $type.small)}>
        <a href='/catalog' class={$type.underline}>
          Каталог
        </a>
        <span>/</span>
        <a href='/catalog/import' class={$type.underline}>
          Импорт
        </a>
        <span>/</span>
        <b>Добавить выгрузку</b>
      </div>

      <div class={clsx($layout.container, $layout.center)}>

        <div class={clsx($layout.collapsible, withData && $layout.collapsed)}>
          <div>
            <h3 class={$.box}>Добавить выгрузку</h3>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <div class={clsx($.importForm)}>
            <form.Field name='file'>
              {(field) => (
                <FormField field={field} footnote='CSV, XLS или XLSX'>
                  <FileInput
                    field={field}
                    label='Файл выгрузки'
                    accept='.csv,.xls,.xlsx,.xml'
                    onValueChange={handleFileChange}
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name='brand'>
              {(field) => (
                <FormField field={field} label='Бренд' footnote='Оставьте пустым для мультрибрендовой выгрузки.'>
                  <SelectInput
                    field={field}
                    options={(brands || []).map((brand) => ({
                      value: brand.id,
                      label: brand.name
                    }))}
                  />
                </FormField>
              )}
            </form.Field>
            <form.Subscribe selector={(state) => [state.isSubmitting]}>
              {([isSubmitting]) => (
                <div class={$form.floatAction}>
                  <button class='secondary' type='submit' disabled={rows.length === 0 || isSubmitting}>
                    {isSubmitting && <Spinner />}
                    {selected.size > 0 ? `Загрузить (${selected.size})` : 'Загрузить'}
                  </button>
                </div>
              )}
            </form.Subscribe>
          </div>

          {error && <p style={{ color: '#dc2626', marginTop: '1em' }}>{error}</p>}
        </form>

        {headers.length > 0 && (
          <p class={$type.small} style={{ marginTop: 0, color: '#666' }}>
            Всего: {rows.length}.{visibleRows.length < rows.length && ` Показано: ${visibleRows.length}.`}{selected.size > 0 && <> <button class={clsx('plain', $type.underline)} onClick={() => {
              let selectedFirst = visibleRows.filter(({ i }) => selected.has(i)).map(({ i }) => i)
              let rest = visibleRows.filter(({ i }) => !selected.has(i)).map(({ i }) => i)
              setPinnedOrder([...selectedFirst, ...rest])
              scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            }}>Выбрано: {selected.size}.</button></>}
          </p>
        )}
      </div>

      {headers.length > 0 && (
        <div class={$.tableScroller} ref={scrollerRef}>
          <table class={clsx($.table, $table.table, $table.condensed, $type.small)}>
            <thead>
              <tr>
                <th>
                  <input
                    type='checkbox'
                    checked={visibleRows.length > 0 && visibleRows.every(({ i }) => selected.has(i))}
                    ref={(el) => {
                      if (el) {
                        let visibleSelected = visibleRows.filter(({ i }) => selected.has(i)).length
                        el.indeterminate = visibleSelected > 0 && visibleSelected < visibleRows.length
                      }
                    }}
                    onChange={() => {
                      let allSelected = visibleRows.every(({ i }) => selected.has(i))
                      if (allSelected) {
                        setSelected(prev => {
                          let next = new Set(prev)
                          visibleRows.forEach(({ i }) => next.delete(i))
                          return next
                        })
                      } else {
                        setSelected(prev => {
                          let next = new Set(prev)
                          visibleRows.forEach(({ i }) => next.add(i))
                          return next
                        })
                      }
                    }}
                  />
                </th>
                {headers.map((h, i) => (
                  <th key={i}>
                    <div class={$layout.row}>
                      {h}
                      {filters[h] && (
                        <button class='plain' onClick={() => setFilters(filters => ({ ...filters, [h]: undefined }))}>
                          <FunnelXIcon />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowVirtualizer.getVirtualItems().length > 0 && (
                <tr style={{ height: rowVirtualizer.getVirtualItems()[0].start }} aria-hidden />
              )}
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                let { row, i } = visibleRows[virtualRow.index]
                return (
                  <tr
                    key={i}
                    style={{
                      height: `${virtualRow.size}px`,
                      opacity: selected.size > 0 && !selected.has(i) ? 0.5 : 1,
                    }}
                  >
                    <td>
                      <input
                        type='checkbox'
                        checked={selected.has(i)}
                        onChange={() => {
                          setSelected((prev) => {
                            let next = new Set(prev)
                            if (next.has(i)) next.delete(i)
                            else next.add(i)
                            return next
                          })
                        }}
                      />
                    </td>
                    {headers.map((header) => (
                      <td
                        key={header}
                        onClick={() => toggleFilter(header, row[header])}
                        title={String(row[header] ?? '')}
                        class={clsx({ [$.filtered]: !!filters[header] })}
                      ><CellValue value={String(row[header] ?? '')} /></td>
                    ))}
                  </tr>
                )
              })}
              {rowVirtualizer.getVirtualItems().length > 0 && (() => {
                let items = rowVirtualizer.getVirtualItems()
                let last = items[items.length - 1]!
                return <tr style={{ height: rowVirtualizer.getTotalSize() - (last.start + last.size) }} aria-hidden />
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
